import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTopicList } from '../api/jueGuangService';
import { ROUTES } from '@app/router/routes';
import { usePageLayout } from '@app/router/index';
import { getStorage, STORAGE_KEYS } from '@shared/api/client';
import AuthModal from '@features/auth/components/AuthModal';
import { useLanguage } from '@shared/contexts/LanguageContext';

// ─── Topic status helpers ─────────────────────────────────────
function getTopicStatus(start_time, end_time, settled) {
  if (settled) return 'settled';
  const now = Date.now();
  const start = new Date(start_time).getTime();
  const end = new Date(end_time).getTime();
  if (now < start) return 'upcoming';
  if (now > end) return 'ended';
  return 'active';
}

function formatDateRange(start_time, end_time) {
  const fmt = (d) => {
    const dt = new Date(d);
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    const h = String(dt.getHours()).padStart(2, '0');
    const min = String(dt.getMinutes()).padStart(2, '0');
    return `${m}/${day} ${h}:${min}`;
  };
  return `${fmt(start_time)} - ${fmt(end_time)}`;
}

// ─── Countdown ────────────────────────────────────────────────
function useCountdown(targetTime) {
  const [diff, setDiff] = useState(0);

  useEffect(() => {
    if (!targetTime) return;
    const update = () => setDiff(Math.max(0, new Date(targetTime).getTime() - Date.now()));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [targetTime]);

  const total = Math.floor(diff / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')} : ${String(m).padStart(2, '0')} : ${String(s).padStart(2, '0')}`;
}

// ─── Topic card ───────────────────────────────────────────────
function TopicCard({ topic, onClick }) {
  const { t } = useLanguage();
  const STATUS_MAP = {
    upcoming: { label: t('jue-guang.statusUpcoming'), bg: 'bg-blue-100', text: 'text-sky-700', timerColor: 'text-sky-700' },
    active: { label: t('jue-guang.statusActive'), bg: 'bg-emerald-100', text: 'text-green-800', timerColor: 'text-stone-800' },
    ended: { label: t('jue-guang.statusEnded'), bg: 'bg-gray-200', text: 'text-neutral-400', timerColor: 'text-stone-400' },
    settled: { label: t('live-badge.settled'), bg: 'bg-purple-100', text: 'text-purple-600', timerColor: 'text-stone-400' },
  };
  const status = getTopicStatus(topic.start_time, topic.end_time, topic.settled);
  const { label, bg, text, timerColor } = STATUS_MAP[status];
  const countdown = useCountdown(status === 'upcoming' ? topic.start_time : null);
  const isDisabled = status === 'upcoming';

  // description: max 2 lines on the card
  const descLines = topic.description.split('\n\n');

  return (
    <div
      className="px-4 py-5 bg-white/80 rounded-lg shadow-[0px_4px_4px_0px_rgba(0,0,0,0.20)] flex justify-start items-center gap-2 cursor-pointer group transition-all duration-200 hover:shadow-md"
      onClick={isDisabled ? undefined : onClick}
    >
      {/* Left content */}
      <div className="flex-1 flex justify-start items-start gap-1.5 min-w-0">
        {/* Gold square icon — aligned to center of first line (text-xl = ~28px line-height) */}
        <div className="shrink-0 flex items-center" style={{ height: '28px' }}>
          <div className="w-3 h-3 bg-[#ffba27]" />
        </div>

        <div className="flex flex-col gap-2.5 min-w-0 flex-1">
          {/* Title + date + badge row */}
          <div className="flex flex-col gap-1 w-full">
            <div className="flex flex-col gap-[5px]">
              <span className="text-xl font-normal text-black group-hover:text-[--color-primary] transition-colors duration-200 line-clamp-1 tracking-wide leading-7">{topic.title}</span>
              <span className="text-sm font-normal text-black tracking-tight">{formatDateRange(topic.start_time, topic.end_time)}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <span className={`px-2.5 py-1 ${bg} rounded-full text-xs font-normal tracking-tight ${text} whitespace-nowrap`}>{label}</span>
              {status === 'upcoming' && (
                <span className={`text-sm font-normal tabular-nums ${timerColor}`}>{countdown}</span>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="text-sm font-light text-black leading-5 tracking-wide">
            {descLines.map((line, i) => (
              <span key={i}>{line}{i < descLines.length - 1 && <><br /><br /></>}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Arrow */}
      <div className={`w-9 h-9 p-1 rounded-3xl flex justify-center items-center shrink-0 ${isDisabled ? 'bg-[#d9d9d9]/60 cursor-not-allowed' : 'bg-gradient-to-br from-[#d9d9d9]/30 to-[#d8d8d8]/30'}`}>
        <span className={`material-symbols-outlined ${isDisabled ? 'text-[#C8BDB7]' : 'text-white'}`} style={{ fontSize: '20px' }}>
          arrow_forward_ios
        </span>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────
export default function JueGuangLandingPage() {
  const [topics, setTopics] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [isMember, setIsMember] = useState(!!getStorage(STORAGE_KEYS.MEMBER_TOKEN));
  const [showAuth, setShowAuth] = useState(false);
  const navigate = useNavigate();
  const { setLayoutOptions } = usePageLayout();
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage.code === 'en';

  useEffect(() => {
    setLayoutOptions({ hideFooter: true, noPaddingBottom: true });
    return () => setLayoutOptions({});
  }, []);

  useEffect(() => {
    if (!isMember) return;
    getTopicList().then(res => {
      if (res.success) setTopics(res.data.items ?? []);
      setLoaded(true);
    });
  }, [isMember, currentLanguage]);

  function handleTopicClick(topic) {
    const status = getTopicStatus(topic.start_time, topic.end_time, topic.settled);
    navigate(ROUTES.JUE_GUANG_TOPIC(topic.topic_id), { state: { topicTitle: topic.title, topicEnded: status === 'ended' || status === 'settled' } });
  }

  return (
    <div
      className="min-h-screen"
      style={{ backgroundImage: 'url(/image/jue-guang/background.png)', backgroundSize: 'cover', backgroundPosition: 'top' }}
    >
      {/* Banner + title */}
      <div className="relative">
        <img
          src="/image/jue-guang/banner1.png"
          alt=""
          className="w-full h-[160px] object-cover"
        />
        {/* Title overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <h1 className={`${isEn ? 'text-2xl' : 'text-[42px]'} font-black text-[--color-text-main]`}>{t('jue-guang.pageTitle')}</h1>
        </div>
      </div>

      {/* Topic card list */}
      <div className="flex flex-col gap-4 px-5 pt-4 pb-24">

        {!isMember && (
          <div className="bg-white/80 rounded-2xl p-6 text-center shadow-sm" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <span
              className="material-symbols-outlined text-amber-400 mb-3 block"
              style={{ fontVariationSettings: "'FILL' 1", fontSize: '48px' }}
            >
              lock
            </span>
            <p className="text-base font-bold text-stone-700 mb-2">{t('jue-guang-topic.membersOnlyTitle')}</p>
            <p className="text-sm text-stone-500 mb-4">{t('jue-guang-topic.landingMembersOnlyDesc')}</p>
            <button
              onClick={() => setShowAuth(true)}
              className="inline-flex items-center gap-2 border border-amber-400 text-amber-600 bg-white text-sm font-medium px-6 py-2.5 rounded-full transition-all duration-200 hover:bg-amber-50 cursor-pointer"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>login</span>
              {t('jue-guang-topic.loginRegister')}
            </button>
          </div>
        )}

        <AuthModal
          open={showAuth}
          onClose={() => setShowAuth(false)}
          onSuccess={() => {
            setIsMember(true);
            setShowAuth(false);
          }}
          defaultMode="login"
        />

        {isMember && topics.map((topic) => (
          <TopicCard
            key={topic.topic_id}
            topic={topic}
            onClick={() => handleTopicClick(topic)}
          />
        ))}

        {isMember && loaded && topics.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <span
              className="font-['Noto_Serif_TC'] text-base font-normal tracking-[0.2em]"
              style={{ color: 'rgba(188,49,61,0.5)' }}
            >
              {t('jue-guang.comingSoon')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
